
/**
 * Generates a unique device fingerprint based on browser characteristics
 * This is used to help identify suspicious activity and rate limiting
 * @returns A fingerprint string that represents the device
 */
export function getDeviceFingerprint(): string {
  try {
    const components = [
      navigator.userAgent,
      navigator.language,
      navigator.platform,
      navigator.hardwareConcurrency?.toString() || '',
      screen.colorDepth?.toString() || '',
      screen.width?.toString() + 'x' + screen.height?.toString(),
      new Date().getTimezoneOffset().toString(),
      !!navigator.plugins?.length?.toString(),
      !!navigator.doNotTrack?.toString(),
      !!window.indexedDB?.toString(),
      !!window.localStorage?.toString(),
      !!window.sessionStorage?.toString()
    ];

    // Generate hash from components
    let hash = 0;
    const combinedString = components.join('###');
    
    for (let i = 0; i < combinedString.length; i++) {
      const char = combinedString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return hash.toString(36); // Convert to base36 to make it shorter
  } catch (error) {
    console.error("Error generating device fingerprint:", error);
    return "unknown-device"; // Fallback if fingerprinting fails
  }
}

/**
 * Get comprehensive device info for security logging
 */
export function getDeviceInfo() {
  try {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenSize: `${screen.width}x${screen.height}`,
      colorDepth: screen.colorDepth,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeZoneOffset: new Date().getTimezoneOffset(),
      fingerprint: getDeviceFingerprint(),
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error("Error getting device info:", error);
    return {
      userAgent: navigator.userAgent || 'unknown',
      fingerprint: 'error-getting-fingerprint',
      timestamp: new Date().toISOString()
    };
  }
}

/**
 * Check if the current session might be a bot
 * @returns Probability of being a bot (0-1)
 */
export function getBotProbability(): number {
  try {
    let botScore = 0;
    
    // Check for common bot characteristics
    if (!navigator.cookieEnabled) botScore += 0.2;
    if (navigator.webdriver) botScore += 0.6;
    if (navigator.userAgent.toLowerCase().includes('headless')) botScore += 0.7;
    if (navigator.plugins.length === 0) botScore += 0.2;
    if (navigator.languages.length === 0) botScore += 0.3;
    
    // Check for consistent timezone
    const tzOffset = new Date().getTimezoneOffset();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    if (!tz) botScore += 0.2;
    if (tzOffset === 0 && !['UTC', 'GMT'].some(t => tz.includes(t))) botScore += 0.2;
    
    // Check for unusual screen dimensions
    const { width, height } = screen;
    if (width < 100 || height < 100) botScore += 0.5;
    
    return Math.min(botScore, 1); // Cap at 1.0
  } catch (error) {
    console.error("Error calculating bot probability:", error);
    return 0; // Fail safe
  }
}
